import { useEffect, useState } from "react";
import { Badge, Box, Button, Card, Group, SimpleGrid, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { IconAlarm, IconArrowsShuffle, IconClock, IconFlame, IconShoppingCart, IconThermometer } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { fmtNumber, titleCase, type GeneratedRecipe, type GeneratedRecipeIngredient, type GeneratedRecipeStep } from "@passio/api-client";
import { FoodIcon } from "./common";
import { FoodDetailDrawer } from "./food";
import { BulletSection, NutritionChips } from "./kitchen";
import { HEAT_COLORS, fmtMinutes, hasNutrition, recipeCartItems, stepTemperature } from "../lib/kitchen";
import type { ShoppingHandoff } from "../pages/Shopping";

const GROUP_ORDER = ["main", "sauce", "seasoning", "garnish", "side"] as const;
const GROUP_LABELS: Record<string, string> = { main: "Main", sauce: "Sauce", seasoning: "Seasoning", garnish: "Garnish", side: "On the side" };

const sectionTitle = (t: string) => (
  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb={4} style={{ letterSpacing: "0.05em" }}>
    {t}
  </Text>
);

function amountText(i: GeneratedRecipeIngredient): string {
  const qty = i.quantity ? `${fmtNumber(i.quantity, 2)}${i.unit ? ` ${i.unit}` : ""}` : i.unit;
  const grams = i.weightGrams ? `${fmtNumber(i.weightGrams)} g` : "";
  return [qty, grams && grams !== qty ? grams : ""].filter(Boolean).join(" · ");
}

function IngredientRow({ ing, onFood }: { ing: GeneratedRecipeIngredient; onFood: (ing: GeneratedRecipeIngredient) => void }) {
  const food = ing.food;
  const kcal = food?.nutritionPreview?.calories;
  const body = (
    <Group gap="sm" wrap="nowrap" className="pd-ing-row" align="flex-start">
      <FoodIcon iconId={food?.iconId} size={32} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap={6} wrap="wrap">
          <Text fz="sm" fw={600}>
            {titleCase(ing.name)}
          </Text>
          {ing.preparation && (
            <Text fz="xs" c="dimmed">
              {ing.preparation}
            </Text>
          )}
          {ing.optional && (
            <Badge size="xs" variant="outline" color="gray">
              optional
            </Badge>
          )}
          {ing.onHand === false && (
            <Badge size="xs" variant="light" color="orange">
              to buy
            </Badge>
          )}
        </Group>
        {food?.displayName ? (
          <Text fz="xs" c="dimmed" truncate>
            Passio: {food.displayName}
          </Text>
        ) : (
          <Text fz="xs" c="dimmed">
            Not linked
          </Text>
        )}
        {ing.notes && (
          <Text fz="xs" c="dimmed" fs="italic">
            {ing.notes}
          </Text>
        )}
      </Box>
      <Box ta="right" style={{ whiteSpace: "nowrap" }}>
        <Text fz="xs" ff="monospace">
          {amountText(ing)}
        </Text>
        {kcal !== undefined && (
          <Text fz={11} ff="monospace" c="dimmed">
            {fmtNumber(kcal)} kcal
          </Text>
        )}
      </Box>
    </Group>
  );
  return food?.refCode ? (
    <UnstyledButton w="100%" onClick={() => onFood(ing)} aria-label={`Nutrition for ${ing.name}`}>
      {body}
    </UnstyledButton>
  ) : (
    <Box>{body}</Box>
  );
}

/** Counts a step's minutes down; tap again to cancel. */
function StepTimer({ minutes }: { minutes: number }) {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (left === null || left <= 0) return;
    const t = window.setTimeout(() => setLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [left]);
  const running = left !== null && left > 0;
  const label = left === null ? `${fmtMinutes(minutes)} timer` : left <= 0 ? "Time's up" : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
  return (
    <Button
      size="compact-xs"
      variant={running ? "filled" : "light"}
      color={left !== null && left <= 0 ? "red" : "violet"}
      leftSection={<IconAlarm size={13} />}
      onClick={() => setLeft(left === null ? Math.round(minutes * 60) : null)}
    >
      {label}
    </Button>
  );
}

function StepRow({ step, index }: { step: GeneratedRecipeStep; index: number }) {
  const temp = stepTemperature(step);
  const heat = step.heat && step.heat !== "none" ? step.heat : null;
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <Badge size="lg" circle color="ink.8" variant="filled" style={{ flex: "none" }}>
        {step.number || index + 1}
      </Badge>
      <Stack gap={4} style={{ flex: 1 }}>
        {step.title && (
          <Text fw={650} fz="sm">
            {step.title}
          </Text>
        )}
        <Text fz="sm">{step.instruction}</Text>
        {(heat || temp || step.durationMinutes) && (
          <Group gap={6}>
            {heat && (
              <Badge size="sm" variant="light" color={HEAT_COLORS[heat] ?? "orange"} leftSection={<IconFlame size={12} />}>
                {heat} heat
              </Badge>
            )}
            {temp && (
              <Badge size="sm" variant="light" color="red" leftSection={<IconThermometer size={12} />}>
                {temp}
              </Badge>
            )}
            {step.durationMinutes ? <StepTimer minutes={step.durationMinutes} /> : null}
          </Group>
        )}
        {step.donenessCue && (
          <Text fz="xs" c="lime.9" bg="lime.0" px={8} py={4} style={{ borderRadius: 6 }}>
            <b>Done when:</b> {step.donenessCue}
          </Text>
        )}
        {step.seasoningNote && (
          <Text fz="xs" c="violet.9" bg="violet.0" px={8} py={4} style={{ borderRadius: 6 }}>
            <b>Season:</b> {step.seasoningNote}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

/**
 * A generated recipe: linked ingredients, steps with heat, timers and doneness cues, tips. Alternates
 * resubmit through `onAlternate`; missing ingredients go to the shopping page.
 */
export function RecipeView({
  recipe,
  missing = [],
  alternates = [],
  alternatesTitle = "Or make one of these",
  onAlternate,
  pendingAlternate,
  cartTitle,
}: {
  recipe: GeneratedRecipe;
  missing?: string[];
  alternates?: string[];
  alternatesTitle?: string;
  onAlternate?: (title: string) => void;
  pendingAlternate?: string | null;
  cartTitle?: string;
}) {
  const navigate = useNavigate();
  const [food, setFood] = useState<GeneratedRecipeIngredient | null>(null);
  const linked = recipe.linkedNutrition;
  const ings = recipe.ingredients ?? [];
  const groups = GROUP_ORDER.map((g) => [g, ings.filter((i) => (i.group ?? "main") === g)] as const).filter(([, list]) => list.length > 0);
  const cart = recipeCartItems(recipe, missing);
  const times = [
    recipe.prepMinutes ? `prep ${fmtMinutes(recipe.prepMinutes)}` : "",
    recipe.cookMinutes ? `cook ${fmtMinutes(recipe.cookMinutes)}` : "",
  ].filter(Boolean);

  return (
    <Stack gap="md">
      <Card>
        <Stack gap="sm">
          <div>
            <Title order={2} fz={24}>
              {recipe.title || "Your recipe"}
            </Title>
            {recipe.description && <Text c="dimmed">{recipe.description}</Text>}
          </div>
          <Group gap={6}>
            {recipe.totalMinutes ? (
              <Badge variant="filled" color="ink.8" leftSection={<IconClock size={12} />}>
                {fmtMinutes(recipe.totalMinutes)}
              </Badge>
            ) : null}
            {times.length > 0 && (
              <Text fz="xs" c="dimmed">
                {times.join(" · ")}
              </Text>
            )}
            <Badge variant="light" color="violet">
              serves {recipe.servings || 1}
            </Badge>
            {recipe.difficulty && <Badge variant="outline" color="gray">{recipe.difficulty}</Badge>}
            {recipe.cuisine && <Badge variant="outline" color="gray">{recipe.cuisine}</Badge>}
            {recipe.mealType && recipe.mealType !== "any" && <Badge variant="outline" color="gray">{recipe.mealType}</Badge>}
            {(recipe.dietTags ?? []).map((t) => (
              <Badge key={t} variant="light" color="lime.8">
                {t}
              </Badge>
            ))}
            {(recipe.allergens ?? []).map((a) => (
              <Badge key={a} variant="outline" color="red">
                contains {a}
              </Badge>
            ))}
          </Group>
          {hasNutrition(linked?.perServing) ? (
            <Stack gap={4}>
              <NutritionChips n={linked!.perServing} label={`per serving, from ${linked!.linkedIngredients ?? 0} of ${linked!.totalIngredients ?? ings.length} ingredients linked to Passio foods`} />
              <NutritionChips n={recipe.estimatedNutritionPerServing} size="xs" label="AI estimate per serving" />
            </Stack>
          ) : (
            <NutritionChips n={recipe.estimatedNutritionPerServing} label="AI estimate per serving (no ingredients linked)" />
          )}
        </Stack>
      </Card>

      <Card>
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <Stack gap="sm">
            {groups.map(([g, list]) => (
              <div key={g}>
                {sectionTitle(GROUP_LABELS[g] ?? g)}
                {list.map((ing, i) => (
                  <IngredientRow key={`${g}-${i}`} ing={ing} onFood={setFood} />
                ))}
              </div>
            ))}
            {(recipe.equipment ?? []).length > 0 && (
              <div>
                {sectionTitle("Equipment")}
                <Text fz="sm">{recipe.equipment!.join(" · ")}</Text>
              </div>
            )}
          </Stack>
          <Stack gap="sm">
            {sectionTitle("Method")}
            {(recipe.steps ?? []).map((s, i) => (
              <StepRow key={i} step={s} index={i} />
            ))}
            <BulletSection title="Tips" items={recipe.tips} />
            {recipe.storage && (
              <div>
                {sectionTitle("Leftovers")}
                <Text fz="sm">{recipe.storage}</Text>
              </div>
            )}
          </Stack>
        </SimpleGrid>
      </Card>

      {cart.length > 0 && (
        <Card bg="var(--pd-cream)">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <div>
              <Text fw={650}>You'll need to buy</Text>
              <Text fz="sm" c="dimmed">
                {cart.map((c) => c.name).join(", ")}
              </Text>
            </div>
            <Button leftSection={<IconShoppingCart size={16} />} onClick={() => navigate("/shopping", { state: { items: cart, title: cartTitle ?? recipe.title ?? "Recipe" } satisfies ShoppingHandoff })}>
              Send to cart
            </Button>
          </Group>
        </Card>
      )}

      {alternates.length > 0 && onAlternate && (
        <Card>
          <Text fw={650} mb={4}>
            {alternatesTitle}
          </Text>
          <Text fz="xs" c="dimmed" mb="sm">
            Titles only. Picking one sends <code>selectedMeal</code> back with the same ingredients and preferences, without re-uploading the media.
          </Text>
          <Group gap="xs">
            {alternates.map((t) => (
              <Button
                key={t}
                variant="light"
                color="violet"
                leftSection={<IconArrowsShuffle size={14} />}
                loading={pendingAlternate === t}
                disabled={Boolean(pendingAlternate) && pendingAlternate !== t}
                onClick={() => onAlternate(t)}
              >
                {t}
              </Button>
            ))}
          </Group>
        </Card>
      )}

      <FoodDetailDrawer
        refCode={food?.food?.refCode}
        name={food?.food?.displayName ?? food?.name}
        amount={food ? { nutritionPreview: food.food?.nutritionPreview, weightGrams: food.weightGrams } : null}
        onClose={() => setFood(null)}
      />
    </Stack>
  );
}
